import {useCallback, useState} from 'react';
import {ChevronDown, Flag, FlagOff, NotebookPen, Tag} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {FlagLabelsDialog, FlagNoteDialog} from './FlagDialogs';
import {cn} from '@/lib/utils';
import {FLAG_COLORS, type FlagColor} from '@/api/flags';
import {useFlags} from '@/api/flagHooks';

// Flag colours are analyst-chosen data, not UI chrome, so they sit outside the design system's
// semantic tokens as the app-local `--flag-*` set (index.css), one per colour and both modes.
// Literal class strings rather than `text-flag-${color}`, so Tailwind's scanner sees every one.
const FLAG_STYLES: Record<FlagColor, {label: string; icon: string}> = {
  red: {label: 'Red', icon: 'text-flag-red fill-flag-red'},
  orange: {label: 'Orange', icon: 'text-flag-orange fill-flag-orange'},
  yellow: {label: 'Yellow', icon: 'text-flag-yellow fill-flag-yellow'},
  green: {label: 'Green', icon: 'text-flag-green fill-flag-green'},
  teal: {label: 'Teal', icon: 'text-flag-teal fill-flag-teal'},
  blue: {label: 'Blue', icon: 'text-flag-blue fill-flag-blue'},
  purple: {label: 'Purple', icon: 'text-flag-purple fill-flag-purple'},
  pink: {label: 'Pink', icon: 'text-flag-pink fill-flag-pink'}
};

// The name to show for a flag: the analyst's own when they've given it one, else the colour's own
// (both come from GET /me/flags). `custom: false` asks for the colour name alone - the placeholder
// in the rename dialog. Falls back to the built-in colour name until the flags have loaded.
export function useFlagLabel() {
  const {data: flags} = useFlags();
  // Stable until the names change, so a memo built on it only recomputes then.
  return useCallback(
    (color: FlagColor, custom = true): string => {
      const flag = flags?.find((f) => f.color === color);
      return (custom ? flag?.name : flag?.default_name) ?? FLAG_STYLES[color].label;
    },
    [flags]
  );
}

export function FlagIcon({
  color,
  className
}: {
  readonly color: FlagColor;
  readonly className?: string;
}) {
  const labelOf = useFlagLabel();
  return (
    <Flag
      role="img"
      aria-label={`${labelOf(color)} flag`}
      className={cn('size-3.5 shrink-0', FLAG_STYLES[color].icon, className)}
    />
  );
}

// The body of every "set a flag" menu - the row's own picker and the bulk bar's share it, so the
// palette always reads in the same order with the same names.
export function FlagMenuItems({
  current,
  showClear,
  onSelect,
  onEditNote,
  onFlagWithNote,
  onNameFlags
}: {
  readonly current?: FlagColor | null;
  readonly showClear: boolean;
  readonly onSelect: (color: FlagColor | null) => void;
  // Offered only where there is a flagged item to annotate.
  readonly onEditNote?: () => void;
  // Offered where an unflagged item can be flagged and annotated in one go.
  readonly onFlagWithNote?: () => void;
  readonly onNameFlags?: () => void;
}) {
  const labelOf = useFlagLabel();
  return (
    <>
      {FLAG_COLORS.map((color) => (
        <DropdownMenuItem
          key={color}
          className={cn(current === color && 'bg-muted')}
          onClick={() => {
            onSelect(color);
          }}>
          <FlagIcon color={color} />
          {labelOf(color)}
        </DropdownMenuItem>
      ))}
      {showClear ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              onSelect(null);
            }}>
            <FlagOff />
            Clear flag
          </DropdownMenuItem>
        </>
      ) : null}
      {onEditNote || onFlagWithNote || onNameFlags ? <DropdownMenuSeparator /> : null}
      {onFlagWithNote ? (
        <DropdownMenuItem onClick={onFlagWithNote}>
          <NotebookPen />
          Flag with note…
        </DropdownMenuItem>
      ) : null}
      {onEditNote ? (
        <DropdownMenuItem onClick={onEditNote}>
          <NotebookPen />
          Add or edit note…
        </DropdownMenuItem>
      ) : null}
      {onNameFlags ? (
        <DropdownMenuItem onClick={onNameFlags}>
          <Tag />
          Name your flags…
        </DropdownMenuItem>
      ) : null}
    </>
  );
}

// A flag trigger and its menu: the current flag's icon, or a faint outline when unflagged. `subject`
// names what is being flagged in the accessible label, for a list where "Set flag" alone would be
// ambiguous; `label` adds visible text beside the icon. The `button` variant is the outlined,
// deliberately subdued peer that sits beside a primary Classify button.
export function FlagPicker({
  value,
  onChange,
  subject,
  label,
  variant = 'icon',
  clearable = value !== null,
  disabled,
  className,
  note,
  onNoteChange
}: {
  readonly value: FlagColor | null;
  // `note` accompanies a colour picked through "Flag with note…" and is otherwise absent.
  readonly onChange: (color: FlagColor | null, note?: string | null) => void;
  // The caller's note on this flag, and how to change it. Passed only where the flagged thing is a
  // real item; without `onNoteChange` the menu has no note entry.
  readonly note?: string | null;
  readonly onNoteChange?: (note: string | null) => void;
  readonly subject?: string;
  readonly label?: string;
  readonly variant?: 'icon' | 'button';
  // Whether the menu offers "Clear flag". Defaults to whether there is a flag to clear; a "flag all"
  // over a mixed set shows no colour but still has flags it can clear.
  readonly clearable?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
}) {
  const labelOf = useFlagLabel();
  const [noteOpen, setNoteOpen] = useState(false);
  const [flagNoteOpen, setFlagNoteOpen] = useState(false);
  const [namesOpen, setNamesOpen] = useState(false);
  const target = subject ? ` for ${subject}` : '';
  const button = variant === 'button';
  const ariaLabel = value
    ? `Flagged ${labelOf(value)}${target} - change flag`
    : `Set flag${target}`;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          aria-label={ariaLabel}
          title={value ? [labelOf(value), note].filter(Boolean).join(': ') : undefined}
          render={
            button ? (
              <Button variant="outline" size="sm" className={className} />
            ) : (
              <Button
                variant="ghost"
                size={label ? 'xs' : 'icon-xs'}
                className={cn('text-muted-foreground', className)}
              />
            )
          }>
          {value ? <FlagIcon color={value} /> : <Flag className={cn(!button && 'opacity-40')} />}
          {note ? <NotebookPen className="size-3" aria-label="Has a note" /> : null}
          {label}
          {button ? <ChevronDown /> : null}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <FlagMenuItems
            current={value}
            showClear={clearable}
            onSelect={onChange}
            {...(value && onNoteChange
              ? {
                  onEditNote() {
                    setNoteOpen(true);
                  }
                }
              : {})}
            {...(!value && onNoteChange
              ? {
                  onFlagWithNote() {
                    setFlagNoteOpen(true);
                  }
                }
              : {})}
            onNameFlags={() => {
              setNamesOpen(true);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {onNoteChange ? (
        <FlagNoteDialog
          open={noteOpen}
          subject={subject ?? 'this item'}
          note={note}
          onOpenChange={setNoteOpen}
          onSave={(newNote) => {
            onNoteChange(newNote);
          }}
        />
      ) : null}
      {onNoteChange && !value ? (
        <FlagNoteDialog
          chooseColor
          open={flagNoteOpen}
          subject={subject ?? 'this item'}
          note={null}
          onOpenChange={setFlagNoteOpen}
          onSave={(newNote, color) => {
            onChange(color, newNote);
          }}
        />
      ) : null}
      <FlagLabelsDialog open={namesOpen} onOpenChange={setNamesOpen} />
    </>
  );
}

// A bulk bar's "Flag ▾" menu over its whole selection - the submissions grid's and the investigations
// workspace's share it, so both read and behave the same.
export function BulkFlagMenu({
  showClear,
  onSelect,
  className
}: {
  readonly showClear: boolean;
  readonly onSelect: (color: FlagColor | null) => void;
  readonly className?: string;
}) {
  const [namesOpen, setNamesOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="outline" size="sm" className={className} />}>
          <Flag />
          Flag
          <ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top">
          <FlagMenuItems
            showClear={showClear}
            onSelect={onSelect}
            onNameFlags={() => {
              setNamesOpen(true);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <FlagLabelsDialog open={namesOpen} onOpenChange={setNamesOpen} />
    </>
  );
}
